import { useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, Trash, FileArrowUp, TextT, ImagesSquare, DownloadSimple, Check } from "@phosphor-icons/react";

/**
 * Bulk import modal for Planner posts and PoV / Ideas.
 *
 * Modes ("post" | "pov") change endpoints and Excel template columns but the UX is identical.
 * Tabs:
 *  - Fields: N textareas the user fills one by one (+ Add another)
 *  - Excel/CSV: upload .xlsx or .csv; we parse client-side and preview before commit
 *  - Images (Planner only): multi-select; each image becomes an empty backlog post
 */
export default function BulkImportModal({ mode = "post", onClose, onDone }) {
  const isPov = mode === "pov";
  const [tab, setTab] = useState("fields"); // fields | file | images
  const [rows, setRows] = useState([{ content: "" }]);
  const [fileRows, setFileRows] = useState([]); // parsed from CSV/xlsx
  const [imageFiles, setImageFiles] = useState([]);
  const [importing, setImporting] = useState(false);

  const columns = isPov
    ? ["content", "kind", "post_type_tag", "tags", "source"]
    : ["topic", "content", "pillar", "post_type_tag", "status", "priority"];

  const csvTemplate = useMemo(() => {
    if (isPov) {
      return "content,kind,post_type_tag,tags,source\n\"Founders who obsess over pricing usually haven't talked to enough customers\",idea,Position,\"pricing,founders\",\"shower thought\"";
    }
    return "topic,content,pillar,post_type_tag,status,priority\nOverthinking pricing,\"Founders overthink pricing because they haven't talked to enough customers.\",growth,Position,Idea,3";
  }, [isPov]);

  const downloadTemplate = () => {
    const blob = new Blob([csvTemplate], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = isPov ? "brandloop-pov-import-template.csv" : "brandloop-posts-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // --- CSV parsing (RFC-4180-ish; handles quoted fields with commas & escaped quotes) ---
  const parseCsv = (text) => {
    const rows = [];
    let cur = [];
    let field = "";
    let inQuotes = false;
    const push = () => { cur.push(field); field = ""; };
    const commit = () => { rows.push(cur); cur = []; };
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (inQuotes) {
        if (c === '"') {
          if (text[i + 1] === '"') { field += '"'; i++; } else inQuotes = false;
        } else field += c;
      } else if (c === '"') inQuotes = true;
      else if (c === ",") push();
      else if (c === "\n") { push(); commit(); }
      else if (c === "\r") { /* ignore */ }
      else field += c;
    }
    push();
    if (cur.length > 1 || cur[0] !== "") commit();
    if (!rows.length) return [];
    const header = rows[0].map(h => h.trim().toLowerCase());
    return rows.slice(1).map(r => {
      const obj = {};
      header.forEach((h, i) => { obj[h] = (r[i] || "").trim(); });
      return obj;
    }).filter(o => (o.content || o.topic || "").trim());
  };

  const handleFile = async (file) => {
    if (!file) return;
    const name = (file.name || "").toLowerCase();
    if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
      try {
        // Lazy-load xlsx (already in bundle via KB source parsing? if not, fallback to CSV instructions)
        const XLSX = await import("xlsx");
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const arr = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        const normed = arr.map(row => {
          const o = {};
          Object.keys(row).forEach(k => { o[String(k).trim().toLowerCase()] = String(row[k] ?? "").trim(); });
          return o;
        }).filter(o => (o.content || o.topic || "").trim());
        setFileRows(normed);
      } catch (e) {
        toast.error("Excel parsing failed. Try saving as CSV, or install xlsx support.");
      }
    } else {
      const text = await file.text();
      setFileRows(parseCsv(text));
    }
  };

  const importFields = async () => {
    const items = rows
      .map(r => ({ content: (r.content || "").trim(), ...r }))
      .filter(r => r.content);
    if (!items.length) { toast.error("Add at least one item."); return; }
    setImporting(true);
    try {
      const url = isPov ? "/povs/bulk" : "/posts/bulk";
      const r = await api.post(url, { items });
      toast.success(`${r.data.created} item${r.data.created === 1 ? "" : "s"} imported.`);
      onDone?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImporting(false); }
  };

  const importFile = async () => {
    if (!fileRows.length) { toast.error("No rows to import — pick a CSV or Excel first."); return; }
    setImporting(true);
    try {
      const url = isPov ? "/povs/bulk" : "/posts/bulk";
      const items = fileRows.map(r => {
        if (isPov) {
          return {
            content: r.content || "",
            kind: (r.kind || "idea").toLowerCase() === "pov" ? "pov" : "idea",
            post_type_tag: r.post_type_tag || r["post type"] || "",
            tags: (r.tags || "").split(",").map(t => t.trim()).filter(Boolean),
            source: r.source || "",
          };
        }
        return {
          topic: r.topic || "",
          content: r.content || "",
          pillar: r.pillar || "",
          post_type_tag: r.post_type_tag || r["post type"] || "",
          status: r.status || "Idea",
          priority: parseInt(r.priority) || 3,
        };
      });
      const rsp = await api.post(url, { items });
      toast.success(`${rsp.data.created} item${rsp.data.created === 1 ? "" : "s"} imported.`);
      onDone?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImporting(false); }
  };

  const importImages = async () => {
    if (!imageFiles.length) { toast.error("Pick at least one image."); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      imageFiles.forEach(f => fd.append("files", f));
      const rsp = await api.post("/posts/bulk-images", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${rsp.data.created} image${rsp.data.created === 1 ? "" : "s"} imported as backlog posts.`);
      onDone?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImporting(false); }
  };

  const TabBtn = ({ id, icon: Icon, children }) => (
    <button
      type="button"
      data-testid={`bulk-tab-${id}`}
      onClick={() => setTab(id)}
      className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-full transition-colors"
      style={tab === id ? { background: "var(--accent)", color: "#fff" } : { background: "var(--paper)", color: "var(--ink-2)" }}
    ><Icon size={13}/> {children}</button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-10 overflow-y-auto" style={{ background: "rgba(0,0,0,0.55)" }} onClick={onClose}>
      <div onClick={(e)=>e.stopPropagation()} className="card w-full max-w-2xl p-6 fade-up" data-testid="bulk-import-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="eyebrow mb-1">Bulk import</div>
            <div className="font-serif text-2xl font-semibold">{isPov ? "PoVs & post ideas" : "Planner backlog"}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[color:var(--paper)]"><X size={16}/></button>
        </div>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <TabBtn id="fields" icon={TextT}>Fields</TabBtn>
          <TabBtn id="file" icon={FileArrowUp}>Excel / CSV</TabBtn>
          {!isPov && <TabBtn id="images" icon={ImagesSquare}>Images</TabBtn>}
        </div>

        {tab === "fields" && (
          <div>
            <div className="text-xs mb-3" style={{ color: "var(--ink-3)" }}>
              One item per field. Post type is suggested automatically after import.
            </div>
            <div className="space-y-2 mb-3 max-h-[420px] overflow-y-auto scroll-thin pr-1" data-testid="bulk-fields-list">
              {rows.map((r, i) => (
                <div key={i} className="p-3 rounded-2xl relative" style={{ background: "var(--bg-soft)", border: "1px solid var(--line)" }}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] font-medium" style={{ color: "var(--ink-3)" }}>Item {i + 1}</span>
                    {rows.length > 1 && (
                      <button
                        type="button"
                        data-testid={`bulk-remove-${i}`}
                        onClick={()=>setRows(rows.filter((_, j) => j !== i))}
                        className="p-1 rounded-full hover:bg-white"
                        style={{ color: "#b83a2b" }}
                      ><Trash size={12}/></button>
                    )}
                  </div>
                  <textarea
                    data-testid={`bulk-content-${i}`}
                    className="textarea w-full"
                    rows={3}
                    placeholder={isPov ? "The take, thought, or post idea…" : "The full post copy…"}
                    value={r.content || ""}
                    onChange={(e)=>setRows(rows.map((x, j) => j === i ? { ...x, content: e.target.value } : x))}
                  />
                </div>
              ))}
            </div>
            <button
              type="button"
              data-testid="bulk-add-another"
              onClick={()=>setRows([...rows, { content: "" }])}
              className="btn btn-ghost text-xs mb-4"
            ><Plus size={12}/> Add another</button>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="btn btn-ghost">Cancel</button>
              <button
                onClick={importFields}
                disabled={importing || !rows.some(r => (r.content || "").trim())}
                data-testid="bulk-import-fields"
                className="btn btn-accent"
              >{importing ? "Importing…" : "Import all"}</button>
            </div>
          </div>
        )}

        {tab === "file" && (
          <div>
            <div className="text-xs mb-2" style={{ color: "var(--ink-3)" }}>
              Upload a CSV or Excel file with these columns (case-insensitive):
            </div>
            <div className="mb-3 p-2 rounded-xl font-mono text-[11px]" style={{ background: "var(--bg-soft)", border: "1px solid var(--line)" }}>
              {columns.join(",  ")}
            </div>
            <div className="mb-3 flex items-center gap-2 flex-wrap">
              <label className="btn btn-ghost cursor-pointer">
                <FileArrowUp size={14}/> Choose file
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls,text/csv"
                  className="hidden"
                  data-testid="bulk-file-input"
                  onChange={(e)=>{ const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value=""; }}
                />
              </label>
              <button type="button" onClick={downloadTemplate} className="btn btn-ghost" data-testid="bulk-download-template">
                <DownloadSimple size={14}/> Download template
              </button>
              {fileRows.length > 0 && <span className="text-xs" style={{ color: "var(--ink-3)" }}>{fileRows.length} row{fileRows.length === 1 ? "" : "s"} ready</span>}
            </div>
            {fileRows.length > 0 && (
              <div className="mb-4 max-h-[280px] overflow-auto scroll-thin rounded-xl" style={{ border: "1px solid var(--line)" }}>
                <table className="w-full text-xs">
                  <thead className="sticky top-0" style={{ background: "var(--paper)" }}>
                    <tr>
                      {columns.map(c => <th key={c} className="text-left px-3 py-2 eyebrow">{c}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {fileRows.slice(0, 30).map((r, i) => (
                      <tr key={i} className="border-t" style={{ borderColor: "var(--line)" }}>
                        {columns.map(c => (
                          <td key={c} className="px-3 py-2 max-w-[240px] truncate" title={r[c] || ""}>{r[c] || ""}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {fileRows.length > 30 && (
                  <div className="text-xs text-center py-2" style={{ color: "var(--ink-3)" }}>+ {fileRows.length - 30} more will import</div>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="btn btn-ghost">Cancel</button>
              <button
                onClick={importFile}
                disabled={importing || !fileRows.length}
                data-testid="bulk-import-file"
                className="btn btn-accent"
              >{importing ? "Importing…" : `Import ${fileRows.length || ""} row${fileRows.length === 1 ? "" : "s"}`}</button>
            </div>
          </div>
        )}

        {tab === "images" && !isPov && (
          <div>
            <div className="text-xs mb-3" style={{ color: "var(--ink-3)" }}>
              Each image becomes a new backlog post. Add copy later.
            </div>
            <label className="cursor-pointer text-sm flex items-center gap-3 mb-4">
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                data-testid="bulk-images-input"
                onChange={(e)=>{ setImageFiles(Array.from(e.target.files || [])); }}
              />
              <div className="btn btn-ghost"><ImagesSquare size={14}/> Choose images</div>
              <span className="text-xs" style={{ color: "var(--ink-3)" }}>PNG / JPG / WebP, up to 8MB each, max 30 files</span>
            </label>
            {imageFiles.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mb-4 max-h-[280px] overflow-auto scroll-thin">
                {imageFiles.slice(0, 20).map((f, i) => (
                  <div key={i} className="rounded-xl p-2 text-xs flex items-center gap-2" style={{ background: "var(--bg-soft)" }}>
                    <Check size={12} style={{ color: "var(--accent)" }}/>
                    <span className="truncate">{f.name}</span>
                  </div>
                ))}
                {imageFiles.length > 20 && <div className="text-xs col-span-4 text-center" style={{ color: "var(--ink-3)" }}>+{imageFiles.length - 20} more selected</div>}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="btn btn-ghost">Cancel</button>
              <button
                onClick={importImages}
                disabled={importing || !imageFiles.length}
                data-testid="bulk-import-images"
                className="btn btn-accent"
              >{importing ? "Uploading…" : `Import ${imageFiles.length || ""} image${imageFiles.length === 1 ? "" : "s"}`}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
